// Human + JSON renderers for the `context` command.
import type { CanonicalSummary, CanonicalContext, MatchFilesResult } from './types'

export function emitJSON(data: unknown): void {
  console.log(JSON.stringify(data, null, 2))
}

function pad(s: string, n: number): string {
  if (s.length >= n) return s
  return s + ' '.repeat(n - s.length)
}

function formatBadges(badges: string[]): string {
  const display: string[] = []
  for (const b of badges) {
    if (b === 'manual' || b === 'fresh') continue
    display.push(`[${b}]`)
  }
  return display.join(' ')
}

export function renderSummaryHuman(items: CanonicalSummary[]): void {
  if (items.length === 0) {
    console.log('No context topics yet.')
    console.log('\nNext:')
    console.log('  driftless context add "auth-boundaries" --what "..." --how "..." --pattern "src/auth/**"')
    return
  }
  const topicWidth = Math.max(...items.map((i) => i.topic.length), 12) + 2
  const badgeWidth = Math.max(...items.map((i) => formatBadges(i.badges).length), 8) + 2
  const statusWidth = Math.max(...items.map((i) => (i.classification?.status || '').length), 8) + 2
  for (const item of items) {
    const topic = pad(item.topic, topicWidth)
    const badges = pad(formatBadges(item.badges), badgeWidth)
    const status = pad(item.classification?.status || '', statusWidth)
    console.log(`${topic}${status}${badges}${item.summary}`)
  }
  console.log(`\n${items.length} topic${items.length === 1 ? '' : 's'}.`)
}

export function renderContextHuman(ctx: CanonicalContext): void {
  console.log(`▌ ${ctx.topic}`)
  console.log(`  ${ctx.summary}`)
  if (ctx.version !== undefined) console.log(`  version:    ${ctx.version}`)
  if (ctx.classification) {
    console.log(`  status: ${ctx.classification.status}`)
  }
  if (ctx.stale.is_stale) {
    // A nudge, not an alarm. Drift tolerates false positives — the framing is
    // "worth a look", not "something is wrong". Trust the code, and only update
    // the topic if the change actually altered how this area works.
    console.log(`\n  ↻ drifted — code under this topic changed since it was last reviewed; worth a look:`)
    console.log(`    ${ctx.stale.reason || 'covered code changed'}`)
    console.log(`  → if it changed how this works: driftless context update ${ctx.topic} --what "..." --how "..."`)
  }

  console.log('')
  if (ctx.description.what) console.log(`  what:       ${ctx.description.what}`)
  if (ctx.description.how) console.log(`  how:        ${ctx.description.how}`)
  if (ctx.description.decisions) {
    // Decisions are append-friendly (--decision repeatable), joined with
    // newlines on write. Render each line as a bullet, mirroring gotchas,
    // so multi-decision topics don't collapse into a wall of text.
    const decisionLines = ctx.description.decisions
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (decisionLines.length === 1) {
      console.log(`  decisions:  ${decisionLines[0]}`)
    } else {
      console.log('\n  decisions')
      for (const d of decisionLines) console.log(`    ◆ ${d}`)
    }
  }
  if (ctx.description.ownership) console.log(`  ownership:  ${ctx.description.ownership}`)

  if (ctx.description.gotchas.length > 0) {
    console.log('\n  gotchas')
    for (const g of ctx.description.gotchas) console.log(`    ! ${g}`)
  }

  if (ctx.content) {
    const lines = ctx.content.split('\n')
    console.log('\n  content')
    // Content-first: the body IS the topic for every kind — show it in full.
    for (const line of lines) console.log(`    ${line}`)
  }
  if (ctx.tags && ctx.tags.length > 0) {
    console.log(`\n  tags: ${ctx.tags.join(', ')}`)
  }

  // Arc 2 — topic graph wiki-links. Forward refs come from [[slug]] in this
  // topic's own text; backlinks come from other topics mentioning this slug.
  // Skip silently when empty so non-graph topics stay quiet.
  const refs = ctx.references_topics ?? []
  if (refs.length > 0) {
    console.log('\n  references')
    for (const r of refs) console.log(`    → ${r}`)
  }
  const backRefs = ctx.referenced_by ?? []
  if (backRefs.length > 0) {
    console.log('\n  referenced by')
    for (const r of backRefs) console.log(`    ← ${r}`)
  }

  if (ctx.anchors.patterns.length > 0 || ctx.anchors.files.length > 0) {
    console.log('\n  anchors')
    for (const p of ctx.anchors.patterns) console.log(`    pattern: ${p}`)
    for (const f of ctx.anchors.files.slice(0, 8)) console.log(`    file:    ${f}`)
    if (ctx.anchors.files.length > 8) {
      console.log(`    ... and ${ctx.anchors.files.length - 8} more files`)
    }
  }

  if (ctx.anchors.repos.length > 0) {
    const names = ctx.anchors.repos.map(
      (id) => ctx.anchors.repo_names?.[id] ?? id.slice(0, 8),
    )
    console.log(
      `\n  used in (${ctx.anchors.repos.length} repo${ctx.anchors.repos.length === 1 ? '' : 's'}): ${names.join(', ')}`,
    )
  }

  // ARP v2 — external anchors (api/mcp/url/…) and structured rules.
  const externalRefs = (ctx.refs ?? []).filter((r) => r.type !== 'file_pattern' && r.type !== 'repo')
  if (externalRefs.length > 0) {
    console.log('\n  external anchors')
    for (const r of externalRefs) {
      const meta = [r['provider'], r['auth']].filter(Boolean).join(' · ')
      console.log(`    ${r.type}: ${r.value}${meta ? `  (${meta})` : ''}`)
    }
  }
  if ((ctx.rules ?? []).length > 0) {
    console.log(`\n  rules (${ctx.rules!.length})`)
    for (const rule of ctx.rules!) {
      const r = rule as Record<string, unknown>
      console.log(`    when: ${String(r['condition'] ?? '')}`)
      if (r['filter']) console.log(`      filter: ${String(r['filter'])}`)
      if (r['action']) console.log(`      action: ${String(r['action'])}`)
      if (Array.isArray(r['fields']) && r['fields'].length) console.log(`      fields: ${(r['fields'] as string[]).join(', ')}`)
      if (r['note']) console.log(`      note: ${String(r['note'])}`)
      if (r['ref']) console.log(`      ref: ${String(r['ref'])}`)
    }
  }

  if (ctx.anchors.docs.length > 0) {
    console.log(`\n  docs (${ctx.anchors.docs.length})`)
    for (const d of ctx.anchors.docs) {
      console.log(`    ${d.path || '(anchored)'}`)
    }
  }

  const components = ctx.components ?? []
  if (components.length > 0) {
    const byRepo = new Map<string, typeof components>()
    for (const c of components) {
      const key = c.repo_id ?? '(unknown)'
      const arr = byRepo.get(key) ?? []
      arr.push(c)
      byRepo.set(key, arr)
    }
    console.log(`\n  components (${components.length})`)
    if (byRepo.size <= 1) {
      const show = components.length > 10 ? 10 : components.length
      for (const c of components.slice(0, show)) {
        console.log(`    ${c.type.padEnd(10)} ${c.file_path}  ${c.name}`)
      }
      if (components.length > 10) console.log(`    ... and ${components.length - 10} more`)
    } else {
      for (const [repoId, comps] of byRepo) {
        const label =
          ctx.anchors.repo_names?.[repoId] ??
          (repoId === '(unknown)' ? '(unknown repo)' : repoId.slice(0, 8))
        console.log(`    ▸ ${label} (${comps.length})`)
        const show = comps.length > 6 ? 6 : comps.length
        for (const c of comps.slice(0, show)) {
          console.log(`        ${c.type.padEnd(10)} ${c.file_path}  ${c.name}`)
        }
        if (comps.length > 6) console.log(`        ... and ${comps.length - 6} more`)
      }
    }
  }

  if (ctx.invariants.length > 0) {
    console.log('\n  invariants')
    for (const inv of ctx.invariants) console.log(`    ◆ ${inv}`)
  }

  if (ctx.required_checks.length > 0) {
    console.log('\n  required checks')
    for (const chk of ctx.required_checks) console.log(`    ✓ ${chk}`)
  }

  if (ctx.events.length > 0) {
    console.log(`\n  events (${ctx.events.length})`)
    for (const e of ctx.events.slice(0, 5)) {
      console.log(`    ${e.source.padEnd(7)} ${e.event_type}  ${e.created_at}`)
    }
    if (ctx.events.length > 5) console.log(`    ... and ${ctx.events.length - 5} more`)
  }

  console.log(`\n  source:     ${ctx.metadata.source}`)
  if (ctx.metadata.last_updated) console.log(`  updated:    ${ctx.metadata.last_updated}`)
  if (ctx.metadata.created_by) console.log(`  created_by: ${ctx.metadata.created_by}`)
}

export function renderMatchFilesHuman(results: MatchFilesResult[], files: string[]): void {
  if (results.length === 0) {
    console.log(`No matching context for ${files.length} changed file${files.length === 1 ? '' : 's'}.`)
    return
  }
  console.log(`Matched ${results.length} topic${results.length === 1 ? '' : 's'} for ${files.length} file${files.length === 1 ? '' : 's'}:\n`)
  // Freshness is a lazy badge surfaced right here, at the before-edit moment,
  // so you don't need a separate `sync` ritual to learn a topic drifted.
  const staleCount = results.filter((r) => r.context.stale.is_stale).length
  if (staleCount > 0) {
    console.log(`⚠ ${staleCount} of these drifted since last reviewed — context may be behind the code. Trust the code, then update the topic.\n`)
  }
  for (const r of results) {
    const ctx = r.context
    console.log(`▌ ${ctx.topic}  (${r.match_reason})`)
    console.log(`  ${ctx.summary}`)
    if (ctx.stale.is_stale) {
      console.log(`  ↻ drifted — ${ctx.stale.reason || 'covered code changed'} (worth a look)`)
    }
    if (ctx.description.what) console.log(`  what: ${ctx.description.what}`)
    if (ctx.description.gotchas.length > 0) {
      for (const g of ctx.description.gotchas) console.log(`  ! ${g}`)
    }
    console.log('')
  }
}
